/* 2026-10-29 (draft): Add buttons show a check (aria-label "Added") after a successful add and revert after 2s or on failure; never left disabled.
   2026-10-22 (draft): select change also swaps the card image to the chosen variant's image.
   2026-10-20 (draft) Kestrel cart-drawer add-ons: one-tap Cart AJAX add, then let the theme's
   cart-items-component re-render the drawer via CartLinesUpdateEvent (same flow as product-form.js). */
import { CartLinesUpdateEvent } from '@shopify/events';

const CHECK_SVG = '<svg class="k-add-check" aria-hidden="true" focusable="false" width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M3 8.5l3.2 3.2L13 4.5"/></svg>';
const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

class KestrelCartAddons extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('change', this.onChange);
  }
  disconnectedCallback() {
    this.removeEventListener('click', this.onClick);
    this.removeEventListener('change', this.onChange);
  }
  onChange = (event) => {
    const select = event.target.closest('[data-addon-select]');
    if (!select) return;
    const card = select.closest('[data-addon]');
    const button = card && card.querySelector('[data-addon-add]');
    if (button) button.dataset.variantId = select.value;
    const price = card && card.querySelector('[data-addon-price]');
    const opt = select.selectedOptions[0];
    if (price && opt) price.textContent = opt.dataset.price;
    // 2026-10-22: show the chosen variant's own image (options carry data-image / data-srcset).
    if (card && opt && opt.dataset.image) {
      let img = card.querySelector('[data-addon-img], .k-addon__img');
      if (!img) {
        const media = card.querySelector('.k-addon__media');
        if (media) {
          img = document.createElement('img');
          img.className = 'k-addon__img';
          img.setAttribute('data-addon-img', '');
          img.width = 64; img.height = 64; img.alt = ''; img.sizes = '64px';
          media.appendChild(img);
        }
      }
      if (img) {
        img.loading = 'eager';
        if (opt.dataset.srcset) img.srcset = opt.dataset.srcset; else img.removeAttribute('srcset');
        img.src = opt.dataset.image;
      }
    }
  };
  /** Put the button back to its resting label (idempotent; always leaves it enabled). */
  resetButton(button, state) {
    if (!button || !button.isConnected) return;
    clearTimeout(state.timer);
    button.classList.remove('is-added');
    button.innerHTML = state.label;
    if (state.aria === null) button.removeAttribute('aria-label'); else button.setAttribute('aria-label', state.aria);
    button.style.minWidth = state.minWidth;
    button.removeAttribute('aria-busy');
    button.disabled = false;
    delete button.dataset.kestrelAdded;
  }
  onClick = async (event) => {
    const button = event.target.closest('[data-addon-add]');
    if (!button || button.disabled) return;
    event.preventDefault();
    if (button.dataset.kestrelAdded) return; // check mark showing: the button is back in 2s
    const variantId = button.dataset.variantId;
    if (!variantId) return;
    const msg = this.querySelector('[data-addon-msg]');
    const state = {
      label: button.innerHTML,
      aria: button.getAttribute('aria-label'),
      minWidth: button.style.minWidth,
      timer: 0,
    };
    // Keep the button's width while the label swaps to an icon, so the row does not shift.
    button.style.minWidth = `${button.offsetWidth}px`;
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.textContent = 'Adding…';
    if (msg) msg.textContent = '';

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId).filter(Boolean);
    const deferred = CartLinesUpdateEvent.createPromise();
    button.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: [{ merchandiseId: String(variantId), quantity: 1 }],
      promise: deferred.promise,
    }));

    try {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await res.json();
      const cart = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());
      const failed = !res.ok || data.status;
      if (!failed && button.isConnected) {
        // Success: check mark (theme olive + bone), "Added" for assistive tech, usable again right away.
        button.classList.add('is-added');
        button.innerHTML = CHECK_SVG;
        button.setAttribute('aria-label', 'Added');
        button.removeAttribute('aria-busy');
        button.disabled = false;
        button.dataset.kestrelAdded = 'true';
        if (msg) msg.textContent = 'Added to cart.';
        state.timer = setTimeout(() => this.resetButton(button, state), 2000);
        // In the drawer the cart re-render removes this card; hold it briefly so the check is seen.
        if (button.closest('cart-items-component')) await new Promise((r) => setTimeout(r, 600));
      }
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'kestrel-cart-addons',
          itemCount: 1,
          sections: failed ? undefined : data.sections,
          didError: Boolean(failed),
        },
      });
      document.dispatchEvent(new CustomEvent('cart:update', { detail: { source: 'kestrel-cart-addons' } }));
      if (failed) throw new Error(data.description || data.message || 'Add failed');
    } catch (error) {
      console.warn('[kestrel-cart-addons]', error);
      this.resetButton(button, state);
      if (msg && msg.isConnected) msg.textContent = 'Couldn’t add that item. Please try again.';
    }
  };
}

customElements.get('kestrel-cart-addons') || customElements.define('kestrel-cart-addons', KestrelCartAddons);
